import React from 'react';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { TextField, Select } from 'formik-material-ui';
import * as Yup from 'yup';
import codes from 'country-calling-code';
import { find } from 'lodash';

import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import * as UserService from '../../api/userService';

import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';
import useCompanyContext from '../../hooks/useCompanyContext';

const useStyles = makeStyles((theme) => ({
  content: {
    [theme.breakpoints.up('sm')]: {
      width: 400,
      margin: '0 auto',
      marginTop: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      margin: '0 auto',
      marginTop: theme.spacing(2),
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
  },
  header: {
    marginTop: theme.spacing(2),
  },
  actionContainer: {
    marginTop: 12,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  box: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  close: {
    marginRight: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  titleContainer: {
    marginRight: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

export interface Values {
  mobile: string;
  countryCode: string;
}

const ValidationSchema = Yup.object().shape({
  countryCode: Yup.string().required('Required'),
  mobile: Yup.string().required('Mobile Number is required'),
});

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
  onSuccess: () => void;
  currentMobNum: string;
  userId: string;
};

export default function UpdateMobileDialog({
  open,
  onClose,
  onSuccess,
  currentMobNum,
  userId,
}: Props) {
  const classes = useStyles();
  const { company } = useCompanyContext();
  const currentCountryCode = find(codes, function (code) {
    return code.countryCodes[0] === currentMobNum.split(' ')[0].substring(1);
  });

  const initialValues: Values = {
    mobile: currentMobNum.length > 0 ? currentMobNum.split(' ')[1] : '',
    countryCode: currentCountryCode ? currentCountryCode.isoCode2 : 'US',
  };

  const handleSubmit = async (
    values: Values,
    { setFieldError, setSubmitting }: FormikHelpers<Values>
  ) => {
    console.log('submit mobile');
    if (company && userId && values.mobile && values.countryCode) {
      const callingCode = find(codes, function (code) {
        return code.isoCode2 === values.countryCode;
      });
      if (callingCode) {
        const phoneNum =
          '+' + callingCode.countryCodes + ' ' + values.mobile.trim();
        try {
          await UserService.updateUserMobile({
            companyId: company.id,
            mobNum: phoneNum,
            userID: userId,
          });
          onSuccess();
          setSubmitting(false);
        } catch (e: any) {
          console.log('Error: ', e);
          e.data.forEach((err: any) => {
            setFieldError(err.meta.attribute, err.detail);
          });
          setSubmitting(false);
        }
      }
    }
  };

  // TODO - Display a loader
  if (!company) {
    return null;
  }

  return (
    <FullScreenDialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form noValidate>
            <div className={classes.box}>
              <IconButton onClick={onClose}>
                <CloseIcon className={classes.closeIcon} />
              </IconButton>
            </div>
            <Grid container>
              <Grid item xs={12}>
                <div className={classes.content}>
                  <Container maxWidth="md">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <div className={classes.titleContainer}>
                          <Typography variant="h4">
                            Edit Mobile Number
                          </Typography>
                        </div>
                      </Grid>
                      <Grid item xs={5}>
                        <Field
                          component={Select}
                          name="countryCode"
                          label=""
                          fullWidth
                        >
                          {codes.map((code) => (
                            <MenuItem value={code.isoCode2} key={code.isoCode2}>
                              {'+' + code.countryCodes + ' ' + code.country}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>
                      <Grid item xs={7}>
                        <Field
                          component={TextField}
                          name="mobile"
                          label=""
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <div className={classes.actionContainer}>
                          <Button onClick={onClose}>Cancel</Button>
                          <Button
                            type="submit"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            Save
                          </Button>
                        </div>
                      </Grid>
                    </Grid>
                  </Container>
                </div>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </FullScreenDialog>
  );
}
